/**
 * The itemised lines of a quote — the ONE derivation of "what does each thing in
 * this box cost at this order quantity".
 *
 * # Why this is a module and not two loops
 *
 * Chapter 10 (F11): a customer now reads an itemised review immediately before
 * committing, alongside the same itemised list on the quote page. If the review
 * did its own arithmetic, the review and the page could quote different prices
 * for one order. That is not hypothetical — P3a found exactly that failure
 * shipped, with the screen rounding totals to whole euros while the email
 * carried two decimals. One derivation, two renderers.
 *
 * The rules here are the quote page's existing rules, moved rather than
 * rewritten:
 *
 *   - an item whose `minBoxes` exceeds the order quantity is charged NOTHING and
 *     flagged, because the customer cannot have it yet;
 *   - otherwise the unit price is the band `getTieredPrice` resolves for that
 *     quantity;
 *   - the line total is the unit price times the ORDER quantity;
 *   - nothing is rounded. Rounding belongs to `formatMoney` at the point of
 *     render, never to a computed value.
 *
 * Those are the same rules `computeUnitBoxPrice` applies when it produces
 * `cart.pricing`, so `sumLineTotals` agrees with the order total by
 * construction. `reviewSummary.test.ts` asserts that agreement directly rather
 * than trusting the resemblance.
 */

import { getTieredPrice } from './pricing';
import type { Option } from '../types';

/**
 * A selected thing, as the quote page already resolves it: an authored option
 * plus the label of the step it came from. Every field but `id` is optional
 * because the "Other…" free-text row is synthesised, not authored.
 */
export type ReviewableItem = Partial<Option> & {
  id: string;
  /** The owning step's display label, e.g. "MERCH". */
  stepLabel?: string;
};

export interface ReviewLine {
  readonly id: string;
  readonly label: string;
  readonly stepLabel: string;
  readonly image?: string;
  /** Price of one box's worth of this item at the order quantity. */
  readonly unitPrice: number;
  /** `unitPrice` x the order quantity. Never rounded. */
  readonly lineTotal: number;
  /** The order quantity is below this item's own minimum. */
  readonly unavailable: boolean;
  readonly minBoxes?: number;
}

/** The itemised lines for `items` at an order quantity of `qty`. */
export function buildReviewLines(items: readonly ReviewableItem[], qty: number): ReviewLine[] {
  return items.map((item) => {
    const minBoxes = item.minBoxes;
    const unavailable = !!minBoxes && qty < minBoxes;
    const unitPrice = unavailable ? 0 : getTieredPrice(item as Option, qty);
    return {
      id: item.id,
      // A quote row with no name is worse than a quote row naming a raw id.
      label: item.label && item.label.length > 0 ? item.label : item.id,
      stepLabel: item.stepLabel ?? '',
      ...(item.image ? { image: item.image } : {}),
      unitPrice,
      lineTotal: unitPrice * qty,
      unavailable,
      ...(minBoxes ? { minBoxes } : {}),
    };
  });
}

/** The order total these lines add up to. Zero for an empty box, never NaN. */
export function sumLineTotals(lines: readonly ReviewLine[]): number {
  return lines.reduce((total, line) => total + line.lineTotal, 0);
}
